import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Box, CircularProgress } from '@mui/material';

declare global {
  interface Window {
    hcaptcha: {
      render: (container: string | HTMLElement, options: {
        sitekey: string;
        callback?: (token: string) => void;
        'error-callback'?: (error: string) => void;
        'expired-callback'?: () => void;
      }) => string;
      reset: (widgetId?: string) => void;
      execute: (widgetId?: string) => void;
      remove: (widgetId?: string) => void;
    };
  }
}

interface HCaptchaProps {
  siteKey: string;
  onVerify: (token: string) => void;
  onError?: (error: string) => void;
  onExpire?: () => void;
  theme?: 'light' | 'dark';
  size?: 'normal' | 'compact' | 'invisible';
}

export interface HCaptchaRef {
  reset: () => void;
}

const HCaptcha = React.forwardRef<HCaptchaRef, HCaptchaProps>(({
  siteKey,
  onVerify,
  onError,
  onExpire,
  theme = 'light',
  size = 'normal',
}, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check if hCaptcha script is loaded
    const checkHCaptcha = () => {
      if (window.hcaptcha) {
        setIsLoaded(true);
        setIsLoading(false);
        return true;
      }
      return false;
    };

    // Check immediately
    if (checkHCaptcha()) {
      return;
    }

    // Poll for hCaptcha to load
    const interval = setInterval(() => {
      if (checkHCaptcha()) {
        clearInterval(interval);
      }
    }, 100);

    // Cleanup
    return () => {
      clearInterval(interval);
    };
  }, []);

  // Memoize callbacks to prevent unnecessary re-renders
  const handleVerify = useCallback((token: string) => {
    onVerify(token);
  }, [onVerify]);

  const handleError = useCallback((error: string) => {
    if (onError) {
      onError(error);
    }
  }, [onError]);

  const handleExpire = useCallback(() => {
    if (onExpire) {
      onExpire();
    }
  }, [onExpire]);

  useEffect(() => {
    if (!isLoaded || !containerRef.current || !siteKey) {
      return;
    }

    // Don't re-render if widget already exists
    if (widgetIdRef.current) {
      return;
    }

    try {
      // Render hCaptcha widget
      const widgetId = window.hcaptcha.render(containerRef.current, {
        sitekey: siteKey,
        theme: theme,
        size: size,
        callback: handleVerify,
        'error-callback': handleError,
        'expired-callback': handleExpire,
      });

      widgetIdRef.current = widgetId;
    } catch (error) {
      // console.error('Error rendering hCaptcha:', error);
      if (onError) {
        onError('Failed to load hCaptcha');
      }
      setIsLoading(false);
    }

    // Cleanup function
    return () => {
      if (widgetIdRef.current && window.hcaptcha) {
        try {
          window.hcaptcha.remove(widgetIdRef.current);
        } catch (error) {
          // Ignore cleanup errors
        }
        widgetIdRef.current = null;
      }
    };
  }, [isLoaded, siteKey, theme, size, handleVerify, handleError, handleExpire]);

  const reset = () => {
    if (widgetIdRef.current && window.hcaptcha) {
      window.hcaptcha.reset(widgetIdRef.current);
    }
  };

  // Expose reset method via ref
  React.useImperativeHandle(ref, () => ({
    reset,
  }));

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return <Box ref={containerRef} sx={{ display: 'flex', justifyContent: 'center' }} />;
});

HCaptcha.displayName = 'HCaptcha';

export default HCaptcha;

